import React, {useEffect, useMemo, useRef, useState} from 'react';
import {CircularProgress, Divider} from '@mui/material';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import HistoryIcon from '@mui/icons-material/History';

import api from '../../../../api/axios';
import {RootState, useAppDispatch} from '../../../../store';
import {fetchTradeGroups, toggleTradeGroups} from '../../../../store/slices/tradeGroupsSlice';

import TradeHistoryDesktop from './TradeHistoryDesktop';
import CancelTradeModal, {UserRestrictionDTO} from '../modals/CancelTradeModal';
import TradeHistoryFilterBar from './TradeHistoryFilterBar';
import TradeHistoryPagination from './TradeHistoryPagination';
import {PageResponse, Trade, TradeGroup, TradeStatus, ViewMode} from '../../../../types/trades';

declare global {
  var MOONSKINS_EXTENSION_ENABLED: boolean | undefined;
}

const TradeHistory: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isGrouped = useSelector((state: RootState) => state.tradeGroup.isGroupingEnabled);
  const authStats = useSelector((state: RootState) => state.auth.statistics);

  const [searchParams, setSearchParams] = useSearchParams();

  const mode: ViewMode = location.pathname.includes('purchased') ? 'purchased' : 'sold';
  const urlSearch = searchParams.get('id') || '';

  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(250);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [statusFilter, setStatusFilter] = useState<string[]>(['ALL']);

  const [counts, setCounts] = useState<{sold: number; purchased: number}>({
    sold: 0,
    purchased: 0
  });

  const [cancelTargets, setCancelTargets] = useState<Trade[]>([]);
  const [userRestrictions, setUserRestrictions] = useState<UserRestrictionDTO | null>(null);

  const hasTradesToAccept = useMemo(() => {
    return mode === 'sold' && trades.some(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM'
    );
  }, [mode, trades]);

  const hasTradesToSend = useMemo(() => {
    return mode === 'sold' && trades.some(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND'
    );
  }, [mode, trades]);

  const hasTradesToCancel = useMemo(() => {
    if (mode === 'purchased') {
      return trades.some(
          (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM'
      );
    }

    return trades.some((trade) => {
      const status = trade.effectiveTradeStatus;

      return status === 'WAITING_FOR_SELLER_CONFIRM' || status === 'WAITING_FOR_SELLER_SEND';
    });
  }, [mode, trades]);

  const hasSteamOffersToAccept = useMemo(() => {
    return mode === 'purchased' && trades.some(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_BUYER_ACCEPT'
    );
  }, [mode, trades]);

  useEffect(() => {
    const checkExtension = () => {
      if (globalThis.MOONSKINS_EXTENSION_ENABLED === true) {
        setIsExtensionInstalled(true);
      }
    };

    checkExtension();

    globalThis.addEventListener('MOONSKINS_EXTENSION_INSTALLED', checkExtension);

    return () => {
      globalThis.removeEventListener('MOONSKINS_EXTENSION_INSTALLED', checkExtension);
    };
  }, []);

  useEffect(() => {
    const handleExtensionSync = (event: MessageEvent) => {
      if (event.data?.target !== 'MOONSKINS_WEBAPP') {
        return;
      }

      if (
          event.data?.action !== 'TRADE_STATUS_UPDATED' &&
          event.data?.action !== 'TRADE_SYNC_REQUIRED'
      ) {
        return;
      }

      void fetchTrades(true);
    };

    window.addEventListener('message', handleExtensionSync);

    return () => {
      window.removeEventListener('message', handleExtensionSync);
    };
  }, [page, pageSize, searchQuery, statusFilter, isGrouped, mode]);

  const fetchTrades = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: (page - 1).toString(),
        size: pageSize.toString(),
        search: searchQuery,
        isGrouped: isGrouped.toString()
      });

      if (statusFilter.length > 0 && !statusFilter.includes('ALL')) {
        statusFilter.forEach((status) => {
          params.append('status', status);
        });
      }

      const response = await api.get<PageResponse<Trade>>(
          `history/${mode}?${params.toString()}`
      );

      setTrades(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Error loading trade history:', error);

      if (!isBackground) {
        setTrades([]);
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  // Fetch counts specifically for open/active trades on both tabs simultaneously
  const fetchOpenCounts = async () => {
    try {
      const soldParams = new URLSearchParams({page: '0', size: '1', search: searchQuery, isGrouped: isGrouped.toString()});
      soldParams.append('status', 'WAITING_FOR_SELLER_CONFIRM');
      soldParams.append('status', 'WAITING_FOR_SELLER_SEND');
      soldParams.append('status', 'WAITING_FOR_BUYER_ACCEPT');

      const purchasedParams = new URLSearchParams({page: '0', size: '1', search: searchQuery, isGrouped: isGrouped.toString()});
      purchasedParams.append('status', 'WAITING_FOR_SELLER_CONFIRM');
      purchasedParams.append('status', 'WAITING_FOR_BUYER_ACCEPT');

      const [soldResponse, purchasedResponse] = await Promise.all([
        api.get<PageResponse<Trade>>(`history/sold?${soldParams.toString()}`),
        api.get<PageResponse<Trade>>(`history/purchased?${purchasedParams.toString()}`)
      ]);

      setCounts({
        sold: soldResponse.data.totalElements,
        purchased: purchasedResponse.data.totalElements
      });
    } catch (error) {
      console.error('Error loading open trade counts:', error);
    }
  };

  // Foreground-Liste: Initial-Load + Query-Änderungen
  useEffect(() => {
    fetchTrades(false);
  }, [mode, page, pageSize, searchQuery, statusFilter, isGrouped]);

  // Counts (open/active trades beider Tabs): Initial-Load + Query-Änderungen
  useEffect(() => {
    void fetchOpenCounts();
  }, [searchQuery, isGrouped]);

  // Live-Refresh im Hintergrund, wenn sich server-seitige Stats ändern
  // (z.B. Trade abgeschlossen). Die initiale Befüllung von authStats via /sync
  // wird übersprungen — die ist bereits durch die beiden Effects oben abgedeckt.
  const skipInitialStatsRefresh = useRef(true);

  useEffect(() => {
    if (!authStats) {
      return;
    }

    if (skipInitialStatsRefresh.current) {
      skipInitialStatsRefresh.current = false;
      return;
    }

    void fetchTrades(true);
    void fetchOpenCounts();
  }, [
    authStats?.totalTrades,
    authStats?.totalSales,
    authStats?.totalPurchases,
    authStats?.totalAvoidedTrades,
    authStats?.totalFailedTrades,
    authStats?.totalVerifiedTrades,
    authStats?.pendingOffers,
    authStats?.actionableTrades
  ]);

  const prevModeRef = useRef(mode);

  useEffect(() => {
    if (prevModeRef.current === mode) {
      return; // Initial-Mount: kein Reset nötig, verhindert doppelten Fetch
    }
    prevModeRef.current = mode;
    setPage(1);
    setStatusFilter(['ALL']);
  }, [mode]);

  useEffect(() => {
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }
  }, [urlSearch, searchQuery]);

  const handleNavigationChange = (newMode: ViewMode) => {
    navigate(`/profile/trades/${newMode}`);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
    setSearchParams(value ? {id: value} : {}, {replace: true});
  };

  const handleStatusFilterChange = (value: string[]) => {
    let newSelection = value;

    if (value.includes('ALL') && value.length > 1) {
      if (!statusFilter.includes('ALL')) {
        newSelection = ['ALL'];
      } else {
        newSelection = value.filter((item) => item !== 'ALL');
      }
    }

    if (newSelection.length === 0) {
      newSelection = ['ALL'];
    }

    setStatusFilter(newSelection);
    setPage(1);
  };

  const handleTradeSuccess = (
      tradeId: string,
      newStatus: string,
      localDate: Date,
      groupUpdates?: {
        tradeGroupId: string;
        tradeGroupLocked: boolean;
      }
  ) => {
    setTrades((previous) =>
        previous.map((trade) =>
            trade.id === tradeId
                ? {
                  ...trade,
                  effectiveTradeStatus: newStatus as TradeStatus,
                  tradeGroupId: groupUpdates
                      ? groupUpdates.tradeGroupId
                      : trade.tradeGroupId,
                  tradeGroupLocked: groupUpdates
                      ? groupUpdates.tradeGroupLocked
                      : trade.tradeGroupLocked
                }
                : trade
        )
    );
  };

  const handleTradeCancel = (trade: Trade) => {
    setCancelTargets([trade]);
  };

  const handleCancelAll = () => {
    const targets = trades.filter((trade) => {
      const status = trade.effectiveTradeStatus;

      if (mode === 'purchased') {
        return status === 'WAITING_FOR_SELLER_CONFIRM';
      }

      return status === 'WAITING_FOR_SELLER_CONFIRM' || status === 'WAITING_FOR_SELLER_SEND';
    });

    if (targets.length === 0) {
      return;
    }

    setCancelTargets(targets);
  };

  const handleAcceptAllTrades = async () => {
    const targets = trades.filter(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM'
    );

    if (targets.length === 0) {
      return;
    }

    const tradeIds = targets.map((trade) => trade.id);

    try {
      const response = await api.post<TradeGroup>(
          '/user/trade/accept',
          {tradeIds}
      );

      const tradeGroup = response.data;

      dispatch(fetchTradeGroups([tradeGroup.id]));

      targets.forEach((trade) => {
        handleTradeSuccess(
            trade.id,
            'WAITING_FOR_SELLER_SEND',
            new Date(),
            {
              tradeGroupId: tradeGroup.id,
              tradeGroupLocked: tradeGroup.locked
            }
        );
      });

      toast.success(t('All eligible trades accepted'));
    } catch (error) {
      console.error('Failed to batch accept trades:', error);
    }
  };

  const handleAcceptAllSteamOffers = () => {
    const targets = trades.filter(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_BUYER_ACCEPT'
    );

    if (targets.length === 0) {
      return;
    }

    const steamTradeIds = targets
        .map((trade) => trade.steamTradeId)
        .filter(Boolean);

    window.postMessage({
      target: 'MOONSKINS_EXTENSION',
      version: 1,
      request_type: 12,
      payload: {
        all: true,
        steamTradeIds
      },
      id: Math.floor(Math.random() * 1000000)
    }, '*');

    toast.info(t('Steam batch processing command dispatched'));
  };

  const handleSendAll = async () => {
    const targets = trades.filter(
        (trade) => trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND'
    );

    if (targets.length === 0) {
      return;
    }

    const tradeIds = targets.map((trade) => trade.id);

    try {
      const response = await api.get('/user/trade/send-details', {
        params: {tradeIds}
      });

      const batchData = response.data;

      for (const tradeData of batchData) {
        window.postMessage({
          target: 'MOONSKINS_EXTENSION',
          version: 1,
          request_type: 11,
          payload: {
            tradeGroupId: tradeData.tradeGroupId,
            toSteamID64: tradeData.buyerSteamId,
            tradeToken: tradeData.tradeToken,
            message: tradeData.message,
            assetIDsToGive: [tradeData.itemAssetId],
            assetIdsToReceive: [],
            forceEnglish: true
          },
          id: Math.floor(Math.random() * 1000000)
        }, '*');

        if (tradeData.tradeId) {
          handleTradeSuccess(
              tradeData.tradeId,
              'WAITING_FOR_BUYER_ACCEPT',
              new Date()
          );
        }
      }

      toast.success(t('All items dispatched to the extension successfully'));
    } catch (error) {
      console.error('Failed batch dispatching item send action:', error);
    }
  };

  return (
      <div className="flex flex-col sm:py-6 sm:px-6 py-2 px-2 space-y-6 bg-white dark:bg-neutral-900/40 shadow-sm border border-gray-100 dark:border-white/5 min-h-[88vh] max-h-[88vh]">

        <TradeHistoryFilterBar
            mode={mode}
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            isGrouped={isGrouped}
            loading={loading}
            counts={counts}
            onViewModeChange={handleNavigationChange}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
            onToggleGrouped={() => dispatch(toggleTradeGroups())}
            onAcceptAllSteamOffers={handleAcceptAllSteamOffers}
            onAcceptAllTrades={handleAcceptAllTrades}
            onSendAll={handleSendAll}
            onCancelAll={handleCancelAll}
            hasTradesToAccept={hasTradesToAccept}
            hasTradesToSend={hasTradesToSend}
            hasTradesToCancel={hasTradesToCancel}
            hasSteamOffersToAccept={hasSteamOffersToAccept}
        />

        <Divider className="!border-gray-100 dark:!border-white/5 shrink-0" />

        <div className="flex-1 flex flex-col min-h-0 bg-transparent">
          {loading ? (
              <div className="flex-1 flex flex-col justify-center items-center py-20 gap-3">
                <CircularProgress
                    size={40}
                    thickness={4}
                    className="text-blue-500 dark:text-dark-primary"
                />

                <span className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-6 animate-pulse">
                  {t('history.actions.processing')}
                </span>
              </div>
          ) : trades.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-20 opacity-30">
                <HistoryIcon
                    sx={{fontSize: 70}}
                    className="text-gray-300 dark:text-gray-600 mb-4"
                />

                <p className="text-lg font-black text-gray-400 uppercase tracking-[0.2em]">
                  {t('history.empty.title')}
                </p>
              </div>
          ) : (
              <div className="overflow-y-auto pr-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                <TradeHistoryDesktop
                    data={trades}
                    mode={mode}
                    isExtensionInstalled={isExtensionInstalled}
                    isGrouped={isGrouped}
                    onSuccess={handleTradeSuccess}
                    onCancel={handleTradeCancel}
                    onRefresh={() => fetchTrades(true)}
                />
              </div>
          )}
        </div>

        <TradeHistoryPagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            totalElements={totalElements}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
        />

        <CancelTradeModal
            open={cancelTargets.length > 0}
            trades={cancelTargets}
            mode={mode}
            restrictions={userRestrictions}
            onClose={() => setCancelTargets([])}
            onSuccess={handleTradeSuccess}
        />
      </div>
  );
};

export default TradeHistory;